import React from 'react';
import Icon from '../../../components/AppIcon';

const TimelineSection = () => {
  const milestones = [
    {
      year: "2019",
      quarter: "Q3",
      title: "AI Clean Core Founded",
      description: "Founded by former Microsoft and Google AI leaders frustrated with vendor lock-in epidemic in enterprise AI.",
      achievements: [
        "Secured $2M seed funding",
        "Assembled world-class founding team",
        "Established core methodology"
      ],
      icon: "Rocket",
      color: "brand-primary"
    },
    {
      year: "2020",
      quarter: "Q2",
      title: "First Major Client Transformation",
      description: "Successfully migrated Fortune 500 manufacturer from proprietary AI stack to open-source, saving $3.2M annually.",
      achievements: [
        "85% cost reduction achieved",
        "Zero downtime migration",
        "Internal team fully trained"
      ],
      icon: "Trophy",
      color: "brand-accent"
    },
    {
      year: "2021",
      quarter: "Q1",
      title: "Open-Source Leadership Recognition",
      description: "Team members became core contributors to TensorFlow, PyTorch, and Kubernetes AI/ML initiatives.",
      achievements: [
        "2M+ lines of code contributed",
        "Speaking at major conferences",
        "Industry thought leadership"
      ],
      icon: "Code2",
      color: "green-500"
    },
    {
      year: "2021",
      quarter: "Q4",
      title: "100 Client Milestone",
      description: "Reached 100 successful AI independence transformations across diverse industries and company sizes.",
      achievements: [
        "100 clients transformed",
        "$50M+ in client savings",
        "95% client satisfaction"
      ],
      icon: "Users",
      color: "blue-500"
    },
    {
      year: "2022",
      quarter: "Q3",
      title: "AI Clean Core Methodology Published",
      description: "Released comprehensive methodology as open-source framework, downloaded by 50k+ organizations.",
      achievements: [
        "50k+ methodology downloads",
        "Industry standard adoption",
        "Academic partnerships"
      ],
      icon: "BookOpen",
      color: "purple-500"
    },
    {
      year: "2023",
      quarter: "Q2",
      title: "Global Expansion",
      description: "Expanded operations to Europe and Asia-Pacific, establishing local teams and partnerships.",
      achievements: [
        "3 new regional offices",
        "Local expert partnerships",
        "Multi-language support"
      ],
      icon: "Globe",
      color: "orange-500"
    },
    {
      year: "2024",
      quarter: "Q1",
      title: "500 Client Transformations",
      description: "Achieved 500 successful AI independence transformations with cumulative client savings exceeding $2.5B.",
      achievements: [
        "500 clients transformed",
        "$2.5B+ total savings",
        "Industry leadership position"
      ],
      icon: "Award",
      color: "brand-primary"
    },
    {
      year: "2024",
      quarter: "Q4",
      title: "AI Independence Institute Launch",
      description: "Launched research institute focused on advancing open-source AI adoption and vendor independence strategies.",
      achievements: [
        "Research institute established",
        "Academic collaborations",
        "Industry research programs"
      ],
      icon: "GraduationCap",
      color: "brand-accent"
    }
  ];

  const currentStats = [
    {
      icon: "Building2",
      value: "500+",
      label: "Enterprise Clients",
      description: "Across 40+ industries"
    },
    {
      icon: "DollarSign",
      value: "$2.5B+",
      label: "Client Savings",
      description: "Cumulative cost reductions"
    },
    {
      icon: "Users",
      value: "150+",
      label: "Team Members",
      description: "Global AI experts"
    },
    {
      icon: "MapPin",
      value: "12",
      label: "Global Offices",
      description: "Across 4 continents"
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-brand-primary/10 rounded-full px-4 py-2 mb-6">
            <Icon name="Clock" size={16} className="text-brand-primary" />
            <span className="text-sm font-medium text-brand-primary">Our Journey</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-6">
            Five Years of
            <span className="block text-brand-primary">Transformational Growth</span>
          </h2>
          
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            From startup to industry leader, our journey reflects the growing demand for AI independence and the success of our open-source approach.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative mb-20">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-brand-primary via-brand-accent to-brand-primary"></div>
          
          <div className="space-y-12">
            {milestones?.map((milestone, index) => (
              <div key={index} className="relative flex items-start space-x-8">
                {/* Timeline Dot */}
                <div className={`relative z-10 w-16 h-16 bg-${milestone?.color} rounded-full flex items-center justify-center shadow-lg`}>
                  <Icon name={milestone?.icon} size={24} className="text-white" />
                </div>
                
                {/* Content */}
                <div className="flex-1 bg-brand-surface rounded-xl p-8 shadow-executive hover:shadow-executive-lg transition-all duration-300">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="text-2xl font-bold text-brand-primary">
                      {milestone?.year}
                    </div>
                    <div className="px-3 py-1 bg-brand-primary/10 text-brand-primary text-sm font-medium rounded-full">
                      {milestone?.quarter}
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-text-primary mb-3">
                    {milestone?.title}
                  </h3>
                  
                  <p className="text-text-secondary mb-6 leading-relaxed">
                    {milestone?.description}
                  </p>
                  
                  <div className="grid md:grid-cols-3 gap-4">
                    {milestone?.achievements?.map((achievement, achievementIndex) => (
                      <div key={achievementIndex} className="flex items-center space-x-2">
                        <Icon name="CheckCircle" size={16} className="text-green-600 flex-shrink-0" />
                        <span className="text-sm text-text-secondary">{achievement}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Current Statistics */}
        <div className="bg-gradient-to-br from-brand-primary to-blue-800 rounded-2xl p-8 lg:p-12 text-white">
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-bold mb-4">
              Where We Stand Today
            </h3>
            <p className="text-blue-100 text-lg">
              Leading the global movement toward AI independence
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {currentStats?.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={stat?.icon} size={32} className="text-brand-accent" />
                </div>
                <div className="text-3xl lg:text-4xl font-bold text-brand-accent mb-2">
                  {stat?.value}
                </div>
                <div className="text-white font-medium mb-2">
                  {stat?.label}
                </div>
                <p className="text-blue-100 text-sm">
                  {stat?.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TimelineSection;